import { Expander } from '#PRIVATE_Expander';
import { Flex } from '#UNSAFE_Flex';
import { useCallback } from 'preact/hooks';
import { useCollapsibleNavigationListContext } from './useCollapsibleNavigationListContext';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { usePress } from '#hooks/UNSAFE_usePress';
import { Text } from '../UNSAFE_Text';
import { Badge } from '../UNSAFE_Badge';
import { WarningS } from '../UNSAFE_RedwoodIcons/WarningS';
import { ErrorS } from '../UNSAFE_RedwoodIcons/ErrorS';
import { InformationS } from '../UNSAFE_RedwoodIcons/InformationS';
import { SuccessS } from '../UNSAFE_RedwoodIcons/SuccessS';
import {
  NavigationListItemStyles,
  NavigationListItemVariantOptions
} from './themes/NavigationListItemStyles.css';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { childItemVars } from './themes/CollapsibleNavigationListItemContract.css';
import { NavigationListItemRedwoodTheme } from './themes/redwood/CollapsibleNavigationListItemTheme';
import { useInteractionStyle } from '#hooks/UNSAFE_useInteractionStyle';
import { useUser } from '#hooks/UNSAFE_useUser';
import { classNames } from '#utils/UNSAFE_classNames';
import { mergeInterpolations } from '#utils/UNSAFE_mergeInterpolations';
import { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import { Size } from '#utils/UNSAFE_size';

type Severity = 'warning' | 'info' | 'none' | 'error' | 'confirmation';
type ToggleDetail<K> = {
  value: K;
};
type CollapsibleNavigationListItemProps<K extends string | number> = {
  itemKey: any;
  item: any;
  label: any;
  expandedKeys?: any;
  level?: any;
  onToggle: (detail: ToggleDetail<K>) => void;
};
export const CollapsibleNavigationListItem = <K extends string | number>({
  item,
  level
}: CollapsibleNavigationListItemProps<K>) => {
  const isGroup = Array.isArray(item.children) && item.children.length > 0;
  const itemKey = item.itemKey;
  const label = item.label;
  const {
    selection,
    onToggle,
    onSelect,
    expandedKeys,
    showFocusRing,
    currentKey,
    navigationListItemPrefix
  } = useCollapsibleNavigationListContext();
  const isExpanded = isGroup && expandedKeys?.has(item.itemKey);
  const handlePress = useCallback(() => {
    onSelect?.({ value: item.itemKey as K });
    if (isGroup) {
      onToggle?.({ value: item.itemKey as K });
    }
  }, [itemKey, onSelect, onToggle, isGroup]);
  const { pressProps } = usePress(handlePress);
  const isCurrent = currentKey === itemKey;
  const isSelected = selection === itemKey;

  const { interactionProps, applyPseudoHoverStyle, applyHoverStyle, applyActiveStyle } =
    useInteractionStyle();

  const itemHandlers = { ...mergeProps(pressProps, interactionProps) };
  const itemId = navigationListItemPrefix + itemKey;
  const severityRequirement = !(item.severity === 'none' || item.severity === undefined);
  const isComplexItem = item.metadata || severityRequirement || item.badge;

  //Styling
  const flexItemStyleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const { class: labelContainerFlexItemClasses, ...labelContainerFlexItemStyle } =
    flexItemStyleInterpolations({
      alignSelf: 'center'
    });

  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const {
    classes: listItemClasses,
    styles: { labelContainerClasses, iconLabelContainer, iconContainer, navigationItemMetadata },
    baseTheme
  } = useComponentTheme<NavigationListItemVariantOptions, NavigationListItemStyles>(
    NavigationListItemRedwoodTheme,
    {
      focusRing: showFocusRing ? 'isFocusRing' : 'notFocusRing',
      current: isCurrent ? 'isCurrent' : 'notCurrent',
      selected: isSelected ? 'isSelected' : 'notSelected',
      direction: isRtl ? 'isRtl' : 'notRtl',
      hybridHover: applyHoverStyle ? 'isHybridHover' : 'notHybridHover',
      pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
      active: applyActiveStyle ? 'isActive' : 'notActive'
    }
  );

  const labelContainerStyle = labelContainerFlexItemStyle;
  const itemClasses = classNames([listItemClasses]);

  const navigationItemMatadata = classNames([navigationItemMetadata]);
  // const navigationItemLinkClass = classNames([navigationListItemLink]);

  const endIconContainerClasses = classNames([iconLabelContainer, iconContainer]);

  const indentation = calcIndentation(level);

  const IconComponent = (severityValue: Severity) => {
    switch (severityValue) {
      case 'error':
        return <ErrorS color="danger" />;
      case 'warning':
        return <WarningS color="warning" />;
      case 'info':
        return <InformationS color="info" />;
      case 'confirmation':
        return <SuccessS color="success" />;
      default:
        return null;
    }
  };

  const labelRenderer = () => {
    return (
      <span class={labelContainerClasses} style={labelContainerStyle}>
        <Text size="inherit" weight="inherit" variant="inherit">
          {label}
        </Text>
      </span>
    );
  };
  const complexItemRenderer = () => {
    return (
      <>
        {label && (
          <span class={labelContainerClasses} style={labelContainerStyle}>
            <Text size="inherit" weight="inherit" variant="inherit">
              {label}
            </Text>
          </span>
        )}
        {(item.metadata || severityRequirement || item.badge) && (
          <Flex gap="2x" align="center" justify="end" flex="1 1 auto">
            {' '}
            {item.badge && (
              <span class={endIconContainerClasses}>
                <Badge size="md" variant="neutralSubtle">
                  {item.badge}
                </Badge>
              </span>
            )}
            {item.metadata && (
              <span class={navigationItemMatadata}>
                <Text>{'( ' + item.metadata + ' )'}</Text>
              </span>
            )}
            {item.severityRequirement && (
              <span class={endIconContainerClasses}>{IconComponent(item.severity)}</span>
            )}
          </Flex>
        )}
      </>
    );
  };

  const navlistItemRenderer = () => {
    return (
      <div
        role="treeitem"
        {...itemHandlers}
        aria-expanded={isGroup ? isExpanded : undefined}
        data-oj-key={itemKey}
        id={itemId}
        class={itemClasses}
        aria-selected={isSelected}>
        <div
          style={{ marginInlineStart: indentation as Size, display: 'flex', alignItems: 'center' }}>
          <Flex direction="row" gap="2x" align="center" justify="center">
            {isComplexItem ? complexItemRenderer() : labelRenderer()}
            {isGroup && <Expander itemKey={itemKey} expanded={isExpanded!} onToggle={onToggle!} />}
          </Flex>
        </div>
      </div>
    );
  };

  return (
    <div class={baseTheme}>
      {navlistItemRenderer()}
      {isGroup && isExpanded && (
        <div role="group">
          {item.children.map((child: CollapsibleNavigationListItemProps<K>) => (
            <CollapsibleNavigationListItem
              itemKey={child.itemKey}
              label={child.label}
              item={child}
              expandedKeys={expandedKeys}
              level={level + 1}
              onToggle={onToggle!}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const calcIndentation = (depth: number) => {
  return `calc(${Math.max(0, depth)} * ${childItemVars.childItemPadding} )`;
};
